// common
import {
  VuexModule,
  Module,
  MutationAction,
  getModule,
  Mutation,
} from "vuex-module-decorators";
import store from "@/store";

// api
import {
  getCountryList,
  getLanguageList,
  userCountry,
} from "@/api/common/common";

// helpers
import { ICountry } from "@/interfaces/_shared/common";
import { ICommonState } from "@/interfaces/store/ICommon";
import { ILanguages } from "@/interfaces/_shared/user";
import POSConfigModule from "./pos-config";
import dayjs from "dayjs";

@Module({ dynamic: true, store, name: "common-module", namespaced: true })
class Common extends VuexModule implements ICommonState {
  public countries: { name: string; code: string }[] = [];
  public languages: ILanguages[] = [];
  public userCurrentCountry = "CHF";
  public isCustomerDisplayOpened = false;
  public ticketGroupLoadedDates: Record<number, Date[]> = {};

  get isCustomerScreenOpened(): boolean {
    return this.isCustomerDisplayOpened;
  }

  @Mutation
  public setIsCustomerDisplayOpened(isOpened: boolean) {
    this.isCustomerDisplayOpened = isOpened;
  }

  @Mutation
  public addTicketGroupLoadedDates(data: {
    ticketGroupId: number;
    date: Date;
  }) {
    const { ticketGroupId, date } = data;
    if (!this.ticketGroupLoadedDates[ticketGroupId]) {
      this.ticketGroupLoadedDates[ticketGroupId] = [date];
    } else {
      // So this ticketGroup has selected dates already, lets avoid inserting the same date again
      if (
        !this.ticketGroupLoadedDates[ticketGroupId].find(
          (d) => d.getTime() === date.getTime()
        )
      ) {
        this.ticketGroupLoadedDates[ticketGroupId].push(date);
      }
    }
  }
  @Mutation
  public clearTicketGroupLoadedDates() {
    this.ticketGroupLoadedDates = {};
  }

  @Mutation
  public purgePastTicketGroupDates(today: Date) {
    const todayDayjs = dayjs(today);
    for (const key in this.ticketGroupLoadedDates) {
      this.ticketGroupLoadedDates[key] = this.ticketGroupLoadedDates[
        key
      ].filter((d) => !dayjs(d).isBefore(todayDayjs, "day"));
    }
  }
  /**
   * Fetch 'GET country list' data response
   *
   * @returns {Promise<{ countries: any }>}
   */
  @MutationAction({ mutate: ["countries"] })
  public async fetchCountries(params: {
    lang: string;
  }): Promise<{ countries: any }> {
    let countries: ICountry[] = [];

    try {
      const response = await getCountryList(params);

      countries = response.countries;
    } catch (error) {
      // new NotificationService(error);
    }

    return { countries };
  }

  /**
   * Fetch 'GET country-by-ip' data response
   *
   * @returns {Promise<{ countries: any }>}
   */
  @MutationAction({ mutate: ["userCurrentCountry"] })
  public async fetchUserCountry(): Promise<{
    userCurrentCountry: any;
  }> {
    let userCurrentCountry = "CHF";
    try {
      const response = await userCountry();
      userCurrentCountry = response as string;
    } catch (error) {
      // new NotificationService(error);
    }

    return { userCurrentCountry };
  }

  /**
   * Fetch 'GET country list' data response
   *
   * @returns {Promise<{ countries: any }>}
   */
  @MutationAction({ mutate: ["languages"] })
  public async fetchLanguages(): Promise<{ languages: any }> {
    let languages: ILanguages[] = [];

    try {
      const response = await getLanguageList();

      languages = response.languages;
    } catch (error) {
      // new NotificationService(error);
    }

    return { languages };
  }

  /**
   * Resotres app module fields saved in localstorage
   */
  @Mutation
  restore(commonFields: Partial<ICommonState>) {
    const {
      countries,
      userCurrentCountry,
      languages,
      isCustomerDisplayOpened,
      ticketGroupLoadedDates,
    } = commonFields;

    if (countries) {
      this.countries = countries;
    }

    if (userCurrentCountry) {
      this.userCurrentCountry = userCurrentCountry;
    }

    if (languages) {
      this.languages = languages;
    }

    if (isCustomerDisplayOpened) {
      this.isCustomerDisplayOpened = isCustomerDisplayOpened;
    }

    if (ticketGroupLoadedDates) {
      // this.ticketGroupLoadedDates = ticketGroupLoadedDates;
      // Lets make sure we retrieve dates from localStorage which are in UTC and convert them into user timezone before inserting them into the state
      this.ticketGroupLoadedDates = {};

      for (const key in ticketGroupLoadedDates) {
        // Now lets make sure we retrieve dates from ticketGroupLoadedDates[key] and convert them to user timezone before we save them into this.ticketGroupLoadedDates[key]

        for (let i = 0; i < ticketGroupLoadedDates[key].length; i++) {
          const date = new Date(ticketGroupLoadedDates[key][i]);
          // const userTimezoneOffset = new Date().getTimezoneOffset() * 60000;
          // const localDate = new Date(date.getTime() - userTimezoneOffset);

          if (!this.ticketGroupLoadedDates[key]) {
            this.ticketGroupLoadedDates[key] = [date];
          } else {
            this.ticketGroupLoadedDates[key].push(date);
          }
        }
      }
    }
  }
}

const CommonModule = getModule(Common);
export default CommonModule;
